// src/components/UserManagement.js

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserShield, faTrashAlt, faSpinner, faCheckCircle, faExclamationTriangle,
  faUsers, faUserPlus, faSort, faSortUp, faSortDown, faExternalLinkAlt,
  faEnvelopeCircleCheck, faArrowLeft, faTimes, faShieldAlt, faBell, faBellSlash,
  faUserFriends, faBirthdayCake, faEnvelope, faCalendarAlt, faSearch,
  faChartBar, faChevronDown, faChevronUp, faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { faAndroid, faApple } from '@fortawesome/free-brands-svg-icons';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, Navigate } from 'react-router-dom';

import { db, functions } from '../firebase';
import { collection, getDocs, query, where, Timestamp, orderBy } from 'firebase/firestore';
import { format } from 'date-fns';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../context/AuthContext';

import './UserManagement.css';

const setUserAdminClaimCallable = httpsCallable(functions, 'setUserAdminClaim');
const deleteUserAuthCallable = httpsCallable(functions, 'deleteUserAuth');
const getOrgLoginAnalyticsCallable = httpsCallable(functions, 'getOrgLoginAnalytics');

// Modern confirmation modal component
const ConfirmationModal = ({ show, onClose, onConfirm, title, body, confirmButtonText = 'Confirm', confirmButtonVariant = 'primary', icon, isConfirming }) => {
  if (!show) return null;

  const getButtonClass = () => {
    switch (confirmButtonVariant) {
      case 'success': return 'users-modal-btn-success';
      case 'warning': return 'users-modal-btn-warning';
      case 'danger': return 'users-modal-btn-danger';
      default: return 'users-modal-btn-success';
    }
  };

  const getIconColor = () => {
    switch (confirmButtonVariant) {
      case 'success': return '#22c55e';
      case 'warning': return '#f59e0b';
      case 'danger': return '#ef4444';
      default: return '#3b82f6';
    }
  };

  return (
    <div className="users-modal-backdrop" onClick={isConfirming ? undefined : onClose}>
      <div className="users-modal" onClick={e => e.stopPropagation()}>
        <div className="users-modal-header">
          <h5 className="users-modal-title">
            {icon && <FontAwesomeIcon icon={icon} style={{ color: getIconColor() }} />}
            {title}
          </h5>
          <button
            className="users-modal-close"
            onClick={onClose}
            disabled={isConfirming}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className="users-modal-body">
          {body}
        </div>
        <div className="users-modal-footer">
          <button
            className="users-modal-btn users-modal-btn-secondary"
            onClick={onClose}
            disabled={isConfirming}
          >
            Cancel
          </button>
          <button
            className={`users-modal-btn ${getButtonClass()}`}
            onClick={onConfirm}
            disabled={isConfirming}
          >
            {isConfirming ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin />
                Processing...
              </>
            ) : (
              confirmButtonText
            )}
          </button>
        </div>
      </div>
    </div>
  );
};


// Helper function for sorting - handles null/undefined values and various date formats
const getSortableValue = (value, key) => {
  // Handle null/undefined
  if (value === null || value === undefined) {
    return null;
  }
  // Handle Firestore Timestamp
  if (value instanceof Timestamp) {
    return value.toMillis();
  }
  // Handle serialized Timestamp (from JSON)
  if (value && typeof value === 'object' && typeof value._seconds === 'number') {
    return value._seconds * 1000 + (value._nanoseconds || 0) / 1000000;
  }
  // Handle Date objects
  if (value instanceof Date) {
    return value.getTime();
  }
  // Handle date-related string fields by parsing them
  if (typeof value === 'string') {
    const dateFields = ['creationTime', 'lastSignInTime', 'cDate'];
    if (dateFields.includes(key)) {
      const parsed = new Date(value);
      if (!isNaN(parsed.getTime())) {
        return parsed.getTime();
      }
    }
    return value.toLowerCase();
  }
  // Handle booleans
  if (typeof value === 'boolean') {
    return value ? 1 : 0;
  }
  return value;
};


function UserManagement() {
  const { currentUser, isAdmin, userOrgId, loadingAuth } = useAuth();

  const [users, setUsers] = useState([]);
  const [members, setMembers] = useState([]);
  const [familyMap, setFamilyMap] = useState({});
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState(null);
  const [actionStatus, setActionStatus] = useState({ userId: null, type: null, loading: false });
  const [userMessage, setUserMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('users'); // 'users' or 'unregistered'

  // State for modal
  const [modalConfig, setModalConfig] = useState({
    show: false,
    title: '',
    body: null,
    onConfirm: () => {},
    confirmText: 'Confirm',
    variant: 'primary',
    icon: faShieldAlt,
    isConfirming: false
  });

  // State for sorting
  const [sortConfig, setSortConfig] = useState({ key: 'displayName', direction: 'ascending' });

  // State for search/filter
  const [searchTerm, setSearchTerm] = useState('');

  // State for user activity analytics
  const [activityData, setActivityData] = useState(null);
  const [activityDays, setActivityDays] = useState(90);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [activityError, setActivityError] = useState(null);
  const [showDailyChart, setShowDailyChart] = useState(false);
  const activityCache = useRef({});  // Cache results by days to avoid redundant calls

  // Compute unregistered members (have email but no user account)
  const unregisteredMembers = useMemo(() => {
    const userEmails = new Set(users.map(u => (u.email || '').toLowerCase()).filter(Boolean));
    return members
      .filter(m => {
        const email = (m.email || '').toLowerCase();
        return email && !userEmails.has(email) && m.status !== 'deceased';
      })
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [users, members]);

  const formatDate = (dateValue) => {
    const formatString = 'PP p';
    if (!dateValue) return 'N/A';
    try {
      let dateToFormat;
      if (dateValue instanceof Timestamp) {
        dateToFormat = dateValue.toDate();
      } else if (typeof dateValue === 'string') {
        dateToFormat = new Date(dateValue);
      } else if (dateValue && typeof dateValue._seconds === 'number' && typeof dateValue._nanoseconds === 'number') {
        dateToFormat = new Timestamp(dateValue._seconds, dateValue._nanoseconds).toDate();
      } else if (dateValue instanceof Date) {
        dateToFormat = dateValue;
      } else {
        return 'Invalid Date Input';
      }
      if (isNaN(dateToFormat.getTime())) return 'Invalid Date';
      return format(dateToFormat, formatString);
    } catch (e) {
      console.error("formatDate Error:", e, "Input:", dateValue);
      return 'Error Formatting Date';
    }
  };

  const fetchUsers = useCallback(async () => {
    if (!isAdmin || !userOrgId) {
      setLoadingData(false);
      return;
    }
    setLoadingData(true);
    setError(null);
    setUserMessage({ type: '', text: '' });

    try {
      // Fetch users and members in parallel
      const usersQuery = query(
        collection(db, 'users'),
        where('orgId', '==', userOrgId),
        orderBy('email', 'asc')
      );
      const membersQuery = query(
        collection(db, 'member'),
        where('orgId', '==', userOrgId)
      );
      const familiesQuery = query(
        collection(db, 'family'),
        where('orgId', '==', userOrgId)
      );

      const [userSnapshot, memberSnapshot, familySnapshot] = await Promise.all([
        getDocs(usersQuery),
        getDocs(membersQuery),
        getDocs(familiesQuery)
      ]);

      // Fetch tokens for each user in parallel
      const usersWithTokens = await Promise.all(
        userSnapshot.docs.map(async (docSnapshot) => {
          const userData = { id: docSnapshot.id, ...docSnapshot.data() };
          try {
            const tokensSnapshot = await getDocs(collection(db, 'users', docSnapshot.id, 'tokens'));
            userData.tokenCount = tokensSnapshot.size;
            userData.hasTokens = tokensSnapshot.size > 0;
          } catch {
            userData.tokenCount = 0;
            userData.hasTokens = false;
          }
          return userData;
        })
      );

      // Build family lookup map
      const families = {};
      familySnapshot.docs.forEach(doc => {
        families[doc.id] = doc.data();
      });

      // Get members with their family info
      const membersData = memberSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setUsers(usersWithTokens);
      setMembers(membersData);
      setFamilyMap(families);
      setError(null);
    } catch (err) {
      console.error(`UserManagement: Error fetching users for org ${userOrgId}:`, err);
      if (err.code === 'permission-denied') {
        setError(`Permission denied fetching users. Check Firestore rules.`);
      } else if (err.message && err.message.includes("requires an index")) {
        setError("Database error: A required index is missing for the default sort (email). Please check your Firestore indexes.");
      } else {
        setError(err.message || "An unknown error occurred while fetching users.");
      }
      setUsers([]);
      setMembers([]);
    } finally {
      setLoadingData(false);
    }
  }, [isAdmin, userOrgId]);

  useEffect(() => {
    if (!loadingAuth && isAdmin && userOrgId) {
      fetchUsers();
    } else if (!loadingAuth && (!isAdmin || !userOrgId)) {
      setLoadingData(false);
    }
  }, [loadingAuth, isAdmin, userOrgId, fetchUsers]);

  // Fetch user activity analytics (with client-side cache to prevent redundant calls)
  useEffect(() => {
    if (!isAdmin || !userOrgId) return;

    const cacheKey = `${userOrgId}_${activityDays}`;
    const cached = activityCache.current[cacheKey];
    const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

    if (cached && (Date.now() - cached.timestamp) < CACHE_TTL) {
      setActivityData(cached.data);
      return;
    }

    let cancelled = false;
    const fetchActivity = async () => {
      setLoadingActivity(true);
      setActivityError(null);
      try {
        const result = await getOrgLoginAnalyticsCallable({ days: activityDays });
        if (!cancelled && result?.data?.success) {
          setActivityData(result.data.data);
          activityCache.current[cacheKey] = { data: result.data.data, timestamp: Date.now() };
        }
      } catch (err) {
        console.error('Error fetching org activity analytics:', err);
        if (!cancelled) {
          setActivityError('Failed to load activity data.');
        }
      } finally {
        if (!cancelled) setLoadingActivity(false);
      }
    };

    fetchActivity();
    return () => { cancelled = true; };
  }, [isAdmin, userOrgId, activityDays]);

  // Filter and sort users
  const filteredAndSortedUsers = useMemo(() => {
    // First, filter by search term
    let filteredUsers = users;
    if (searchTerm.trim()) {
      const lowerSearch = searchTerm.toLowerCase().trim();
      filteredUsers = users.filter(user => {
        const name = (user.displayName || user.fullName || '').toLowerCase();
        const email = (user.email || '').toLowerCase();
        return name.includes(lowerSearch) || email.includes(lowerSearch);
      });
    }

    // Then, sort
    let sortableUsers = [...filteredUsers];
    if (sortConfig.key !== null) {
      sortableUsers.sort((a, b) => {
        const aRaw = a[sortConfig.key];
        const bRaw = b[sortConfig.key];
        const aValue = getSortableValue(aRaw, sortConfig.key);
        const bValue = getSortableValue(bRaw, sortConfig.key);

        // Handle cases where one value is null and the other is not
        if (aValue === null && bValue !== null) {
          return sortConfig.direction === 'ascending' ? 1 : -1; // Null values go to the end
        }
        if (aValue !== null && bValue === null) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue === null && bValue === null) {
          // Both null, use secondary sort by email
          const emailA = getSortableValue(a.email, 'email');
          const emailB = getSortableValue(b.email, 'email');
          if (emailA < emailB) return -1;
          if (emailA > emailB) return 1;
          return 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        // Secondary sort by email for boolean columns or when values are equal
        if (sortConfig.key === 'emailVerified' || sortConfig.key === 'admin' || aValue === bValue) {
          const emailA = getSortableValue(a.email, 'email');
          const emailB = getSortableValue(b.email, 'email');
          if (emailA < emailB) return -1;
          if (emailA > emailB) return 1;
        }
        return 0;
      });
    }
    return sortableUsers;
  }, [users, sortConfig, searchTerm]);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return faSort;
    }
    return sortConfig.direction === 'ascending' ? faSortUp : faSortDown;
  };

  const handleCloseModal = () => {
    if (modalConfig.isConfirming) return;
    setModalConfig({ ...modalConfig, show: false });
  };

  const handleToggleAdmin = (userId, userEmail, currentAdminStatus) => {
    const makeAdmin = !currentAdminStatus;
    const actionText = makeAdmin ? 'grant admin privileges to' : 'remove admin privileges from';
    const selfAction = currentUser?.uid === userId;

    setModalConfig({
      show: true,
      title: 'Confirm Admin Status Change',
      body: (
        <p>
          Are you sure you want to {actionText}{' '}
          <strong className={makeAdmin ? 'text-primary' : 'text-danger'}>
            {selfAction ? 'YOURSELF' : userEmail}
          </strong>?
        </p>
      ),
      confirmText: makeAdmin ? 'Grant Admin' : 'Remove Admin',
      variant: makeAdmin ? 'success' : 'warning',
      icon: faUserShield,
      isConfirming: false,
      onConfirm: () => confirmToggleAdmin(userId, userEmail, makeAdmin, selfAction),
    });
  };

  const confirmToggleAdmin = async (userId, userEmail, makeAdmin, selfAction) => {
    setModalConfig(prev => ({ ...prev, isConfirming: true }));
    setActionStatus({ userId: userId, type: 'admin', loading: true });
    setUserMessage({ type: '', text: '' });
    setError(null);

    try {
      const result = await setUserAdminClaimCallable({ userId: userId, setAdminStatus: makeAdmin });
      if (result?.data?.success) {
        setUsers(prevUsers =>
          prevUsers.map(user =>
            user.id === userId ? { ...user, admin: makeAdmin } : user
          )
        );
        setUserMessage({ type: 'success', text: `Successfully ${makeAdmin ? 'granted' : 'removed'} admin privileges for ${userEmail}.` });
        if (selfAction) {
          await currentUser?.getIdTokenResult(true);
        }
      } else {
        throw new Error(result?.data?.message || `Server denied the request to change admin status.`);
      }
    } catch (err) {
      console.error(`Error calling setUserAdminClaim function:`, err);
      setUserMessage({ type: 'danger', text: `Failed to update admin status: ${err.message}` });
    } finally {
      setActionStatus({ userId: null, type: null, loading: false });
      setModalConfig({ show: false, isConfirming: false });
    }
  };

  const handleDeleteUser = (userId, userEmail) => {
    if (currentUser?.uid === userId) {
      setUserMessage({ type: 'warning', text: 'You cannot delete your own account.' });
      return;
    }

    setModalConfig({
      show: true,
      title: 'Delete User',
      body: (
        <>
          <p>Are you sure you want to permanently delete <strong className="text-danger">{userEmail}</strong>?</p>
          <p style={{ fontWeight: 600, color: '#dc2626' }}>
            <FontAwesomeIcon icon={faExclamationTriangle} /> WARNING: This action cannot be undone.
          </p>
        </>
      ),
      confirmText: 'Yes, Delete User',
      variant: 'danger',
      icon: faTrashAlt,
      isConfirming: false,
      onConfirm: () => confirmDeleteUser(userId, userEmail),
    });
  };

  const confirmDeleteUser = async (userId, userEmail) => {
    setModalConfig(prev => ({ ...prev, isConfirming: true }));
    setActionStatus({ userId: userId, type: 'delete', loading: true });
    setUserMessage({ type: '', text: '' });
    setError(null);

    try {
      const result = await deleteUserAuthCallable({ userId: userId });
      if (result?.data?.success) {
        setUsers(prevUsers => prevUsers.filter(user => user.id !== userId));
        setUserMessage({ type: 'success', text: `User "${userEmail}" deleted.` });
      } else {
        throw new Error(result?.data?.message || `Server denied the request to delete user.`);
      }
    } catch (err) {
      console.error(`Error calling deleteUserAuth function:`, err);
      setUserMessage({ type: 'danger', text: `Failed to delete user: ${err.message}` });
    } finally {
      setActionStatus({ userId: null, type: null, loading: false });
      setModalConfig({ show: false, isConfirming: false });
    }
  };

  // --- Render Logic ---

  // Loading auth state
  if (loadingAuth) {
    return (
      <div className="users-page">
        <div className="users-loading">
          <div className="skeleton-header"></div>
          <div className="skeleton-stats"></div>
          <div className="skeleton-table"></div>
        </div>
      </div>
    );
  }

  // Not logged in
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  // Not an admin
  if (!isAdmin) {
    return (
      <div className="users-page">
        <div className="users-header">
          <div className="container">
            <div className="users-header-content">
              <div className="users-header-left">
                <Link to="/dashboard" className="users-back-btn" title="Back to Dashboard">
                  <FontAwesomeIcon icon={faArrowLeft} />
                </Link>
                <div>
                  <h1 className="users-title">Access Denied</h1>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="container">
          <div className="users-access-denied">
            <div className="users-access-card">
              <div className="users-access-icon">
                <FontAwesomeIcon icon={faShieldAlt} />
              </div>
              <h3>Access Denied</h3>
              <p>You do not have permission to view this page. Admin access is required.</p>
              <Link to="/dashboard" className="users-access-btn">
                <FontAwesomeIcon icon={faArrowLeft} />
                Go to Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Missing org ID
  if (!userOrgId) {
    return (
      <div className="users-page">
        <div className="users-header">
          <div className="container">
            <div className="users-header-content">
              <div className="users-header-left">
                <Link to="/dashboard" className="users-back-btn" title="Back to Dashboard">
                  <FontAwesomeIcon icon={faArrowLeft} />
                </Link>
                <div>
                  <h1 className="users-title">Configuration Error</h1>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="container">
          <div className="users-access-denied">
            <div className="users-access-card">
              <div className="users-access-icon">
                <FontAwesomeIcon icon={faExclamationTriangle} />
              </div>
              <h3>Configuration Error</h3>
              <p>Your administrator account is missing necessary organization details. Cannot manage users.</p>
              <Link to="/dashboard" className="users-access-btn">
                <FontAwesomeIcon icon={faArrowLeft} />
                Go to Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error loading users
  if (error) {
    return (
      <div className="users-page">
        <div className="users-header">
          <div className="container">
            <div className="users-header-content">
              <div className="users-header-left">
                <Link to="/dashboard" className="users-back-btn" title="Back to Dashboard">
                  <FontAwesomeIcon icon={faArrowLeft} />
                </Link>
                <div>
                  <h1 className="users-title">User Management</h1>
                  <p className="users-subtitle">Error loading users</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="container">
          <div className="users-alert users-alert-danger">
            <div className="users-alert-icon">
              <FontAwesomeIcon icon={faExclamationTriangle} />
            </div>
            <div className="users-alert-content">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  // Loading users
  if (loadingData && users.length === 0) {
    return (
      <div className="users-page">
        <div className="users-loading">
          <div className="skeleton-header"></div>
          <div className="skeleton-stats"></div>
          <div className="skeleton-table"></div>
        </div>
      </div>
    );
  }

  const totalUserCount = users.length;
  const iosUserCount = users.filter(user => user.platform && user.platform.toLowerCase() === 'ios').length;
  const androidUserCount = users.filter(user => user.platform && user.platform.toLowerCase() === 'android').length;
  const createUsersBaseUrl = "https://www.churchaware.com/create-account/";
  const familyBaseUrl = "https://www.churchaware.com/families/";
  const unregisteredCount = unregisteredMembers.length;

  // Notification statistics
  const usersWithTokens = users.filter(user => user.hasTokens).length;
  const newMemberNotifCount = users.filter(user => user.hasTokens && user.notificationPreferences?.newMemberNotifications !== false).length;
  const birthdayNotifCount = users.filter(user => user.hasTokens && user.notificationPreferences?.birthdayAnniversaryNotifications !== false).length;
  const messageNotifCount = users.filter(user => user.hasTokens && user.notificationPreferences?.messageNotifications !== false).length;
  const eventNotifCount = users.filter(user => user.hasTokens && user.notificationPreferences?.eventNotifications !== false).length;

  return (
    <div className="users-page">
      {/* Modal */}
      <ConfirmationModal
        show={modalConfig.show}
        onClose={handleCloseModal}
        onConfirm={modalConfig.onConfirm}
        title={modalConfig.title}
        body={modalConfig.body}
        confirmButtonText={modalConfig.confirmText}
        confirmButtonVariant={modalConfig.variant}
        icon={modalConfig.icon}
        isConfirming={modalConfig.isConfirming}
      />

      {/* Header */}
      <div className="users-header">
        <div className="container">
          <div className="users-header-content">
            <div className="users-header-left">
              <Link to="/dashboard" className="users-back-btn" title="Back to Dashboard">
                <FontAwesomeIcon icon={faArrowLeft} />
              </Link>
              <div>
                <h1 className="users-title">User Management <span className="admin-only-badge">Admin Only</span></h1>
                <p className="users-subtitle">{totalUserCount} registered {totalUserCount === 1 ? 'user' : 'users'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        {/* Alert Messages */}
        {userMessage.text && (
          <div className={`users-alert users-alert-${userMessage.type || 'info'}`}>
            <div className="users-alert-icon">
              {userMessage.type === 'success' && <FontAwesomeIcon icon={faCheckCircle} />}
              {userMessage.type === 'danger' && <FontAwesomeIcon icon={faExclamationTriangle} />}
              {userMessage.type === 'warning' && <FontAwesomeIcon icon={faExclamationTriangle} />}
            </div>
            <div className="users-alert-content">{userMessage.text}</div>
            <button
              className="users-alert-close"
              onClick={() => setUserMessage({ type: '', text: '' })}
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="users-tabs">
          <button
            className={`users-tab ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            <FontAwesomeIcon icon={faUsers} />
            Registered Users
            <span className="users-tab-count">{totalUserCount}</span>
          </button>
          <button
            className={`users-tab ${activeTab === 'unregistered' ? 'active' : ''}`}
            onClick={() => setActiveTab('unregistered')}
          >
            <FontAwesomeIcon icon={faEnvelope} />
            Not Registered
            <span className="users-tab-count">{unregisteredCount}</span>
          </button>
        </div>

        {activeTab === 'users' && (
          <>
        {/* Stats Cards */}
        <div className="users-stats-grid">
          <div className="users-stat-card">
            <div className="users-stat-header users-stat-header-total">
              <FontAwesomeIcon icon={faUsers} />
              Total Users
            </div>
            <div className="users-stat-body">
              <FontAwesomeIcon icon={faUsers} className="users-stat-bg-icon" />
              <h2 className="users-stat-value">{totalUserCount}</h2>
            </div>
          </div>

          <div className="users-stat-card">
            <div className="users-stat-header users-stat-header-android">
              <FontAwesomeIcon icon={faAndroid} />
              Android Users
            </div>
            <div className="users-stat-body">
              <FontAwesomeIcon icon={faAndroid} className="users-stat-bg-icon" />
              <h2 className="users-stat-value">{androidUserCount}</h2>
            </div>
          </div>

          <div className="users-stat-card">
            <div className="users-stat-header users-stat-header-ios">
              <FontAwesomeIcon icon={faApple} />
              iOS Users
            </div>
            <div className="users-stat-body">
              <FontAwesomeIcon icon={faApple} className="users-stat-bg-icon" />
              <h2 className="users-stat-value">{iosUserCount}</h2>
            </div>
          </div>

          <div className="users-stat-card users-stat-card-invite">
            <div className="users-stat-header users-stat-header-invite">
              <FontAwesomeIcon icon={faUserPlus} />
              Invite Link
            </div>
            <div className="users-stat-body">
              <p className="users-invite-label">Share this link to add users:</p>
              {userOrgId ? (
                <a
                  href={`${createUsersBaseUrl}${userOrgId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="users-invite-link"
                >
                  {`${createUsersBaseUrl}${userOrgId}`}
                </a>
              ) : (
                <span className="users-invite-label">Org ID not found.</span>
              )}
            </div>
          </div>
        </div>

        {/* Push Notification Summary */}
        <div className="users-notification-summary">
          <h3 className="users-notification-title">
            <FontAwesomeIcon icon={faBell} />
            Push Notification Summary
            <span className="users-table-count">{usersWithTokens} devices registered</span>
          </h3>
          <div className="users-notification-grid">
            <div className="users-notification-card">
              <div className="users-notification-icon users-notification-icon-members">
                <FontAwesomeIcon icon={faUserFriends} />
              </div>
              <div className="users-notification-info">
                <span className="users-notification-count">{newMemberNotifCount}</span>
                <span className="users-notification-label">New Member Alerts</span>
              </div>
            </div>
            <div className="users-notification-card">
              <div className="users-notification-icon users-notification-icon-birthday">
                <FontAwesomeIcon icon={faBirthdayCake} />
              </div>
              <div className="users-notification-info">
                <span className="users-notification-count">{birthdayNotifCount}</span>
                <span className="users-notification-label">Birthday/Anniversary</span>
              </div>
            </div>
            <div className="users-notification-card">
              <div className="users-notification-icon users-notification-icon-message">
                <FontAwesomeIcon icon={faEnvelope} />
              </div>
              <div className="users-notification-info">
                <span className="users-notification-count">{messageNotifCount}</span>
                <span className="users-notification-label">Message Alerts</span>
              </div>
            </div>
            <div className="users-notification-card">
              <div className="users-notification-icon users-notification-icon-event">
                <FontAwesomeIcon icon={faCalendarAlt} />
              </div>
              <div className="users-notification-info">
                <span className="users-notification-count">{eventNotifCount}</span>
                <span className="users-notification-label">Event Alerts</span>
              </div>
            </div>
          </div>
        </div>

        {/* User Activity */}
        <div className="users-activity-summary">
          <div className="users-activity-header">
            <h3 className="users-activity-title">
              <FontAwesomeIcon icon={faChartBar} />
              User Activity
            </h3>
            <div className="users-activity-range-toggles">
              {[30, 60, 90].map((d) => (
                <button
                  key={d}
                  className={`users-activity-range-btn ${activityDays === d ? 'active' : ''}`}
                  onClick={() => setActivityDays(d)}
                >
                  {d}d
                </button>
              ))}
            </div>
          </div>
          <p className="users-activity-note">
            <FontAwesomeIcon icon={faInfoCircle} /> Based on users logging in or using the app or website.
          </p>

          {loadingActivity && !activityData && (
            <div className="users-activity-loading">
              <FontAwesomeIcon icon={faSpinner} spin /> Loading activity data...
            </div>
          )}

          {activityError && (
            <div className="users-activity-error">
              <FontAwesomeIcon icon={faExclamationTriangle} /> {activityError}
            </div>
          )}

          {activityData && (
            <>
              {/* Stacked bar chart */}
              <div className="users-activity-bar-container">
                <div className="users-activity-bar-label">
                  {activityData.totalUsers} total users
                </div>
                {activityData.totalUsers > 0 && (
                  <div className="users-activity-stacked-bar">
                    {[
                      { key: 'last24h', label: '24h', color: '#22c55e' },
                      { key: 'last7d', label: '7d', color: '#3b82f6' },
                      { key: 'last30d', label: '30d', color: '#8b5cf6' },
                      { key: 'last90d', label: '90d', color: '#f59e0b' },
                      { key: 'last6mo', label: '6mo', color: '#f97316' },
                      { key: 'inactive', label: 'Inactive', color: '#94a3b8' },
                    ].map(({ key, label, color }) => {
                      const count = activityData.activityBuckets[key];
                      const pct = (count / activityData.totalUsers) * 100;
                      if (count === 0) return null;
                      return (
                        <div
                          key={key}
                          className="users-activity-bar-segment"
                          style={{ width: `${pct}%`, backgroundColor: color }}
                          title={`${label}: ${count} (${Math.round(pct)}%)`}
                        >
                          {pct > 8 && <span className="users-activity-bar-segment-label">{count}</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="users-activity-bar-legend">
                  {[
                    { key: 'last24h', label: 'Last 24h', color: '#22c55e' },
                    { key: 'last7d', label: 'Last 7d', color: '#3b82f6' },
                    { key: 'last30d', label: 'Last 30d', color: '#8b5cf6' },
                    { key: 'last90d', label: 'Last 90d', color: '#f59e0b' },
                    { key: 'last6mo', label: 'Last 6mo', color: '#f97316' },
                    { key: 'inactive', label: 'Inactive', color: '#94a3b8' },
                  ].map(({ key, label, color }) => (
                    <span key={key} className="users-activity-legend-item">
                      <span className="users-activity-legend-dot" style={{ backgroundColor: color }} />
                      {label}: {activityData.activityBuckets[key]}
                    </span>
                  ))}
                </div>
              </div>

              {/* Platform breakdown */}
              <div className="users-activity-platforms">
                <span className="users-activity-platform-item">
                  <FontAwesomeIcon icon={faUsers} /> Web: {activityData.platformBreakdown.web}
                </span>
                <span className="users-activity-platform-item">
                  <FontAwesomeIcon icon={faApple} /> iOS: {activityData.platformBreakdown.ios}
                </span>
                <span className="users-activity-platform-item">
                  <FontAwesomeIcon icon={faAndroid} /> Android: {activityData.platformBreakdown.android}
                </span>
                {activityData.platformBreakdown.unknown > 0 && (
                  <span className="users-activity-platform-item">
                    Unknown: {activityData.platformBreakdown.unknown}
                  </span>
                )}
              </div>

              {/* Daily histogram (expandable) */}
              <button
                className="users-activity-toggle-daily"
                onClick={() => setShowDailyChart(!showDailyChart)}
              >
                <FontAwesomeIcon icon={showDailyChart ? faChevronUp : faChevronDown} />
                {showDailyChart ? 'Hide' : 'Show'} Daily Activity ({activityDays} days)
              </button>

              {showDailyChart && activityData.dailyUniqueLogins && (
                <div className="users-activity-daily-chart">
                  {(() => {
                    const maxCount = Math.max(...activityData.dailyUniqueLogins.map(d => d.count), 1);
                    return activityData.dailyUniqueLogins.map((entry) => (
                      <div
                        key={entry.date}
                        className="users-activity-daily-bar-col"
                        title={`${entry.date}: ${entry.count} user${entry.count !== 1 ? 's' : ''}`}
                      >
                        <div
                          className="users-activity-daily-bar"
                          style={{ height: `${(entry.count / maxCount) * 100}%` }}
                        />
                      </div>
                    ));
                  })()}
                </div>
              )}
              {showDailyChart && activityData.dailyUniqueLogins && (
                <div className="users-activity-daily-labels">
                  <span>{activityData.dailyUniqueLogins[0]?.date}</span>
                  <span>{activityData.dailyUniqueLogins[activityData.dailyUniqueLogins.length - 1]?.date}</span>
                </div>
              )}
            </>
          )}

          {loadingActivity && activityData && (
            <div className="users-activity-refreshing">
              <FontAwesomeIcon icon={faSpinner} spin size="sm" /> Refreshing...
            </div>
          )}
        </div>

        {/* Users Table */}
        <div className="users-table-card">
          <div className="users-table-header">
            <h3 className="users-table-title">
              <FontAwesomeIcon icon={faUsers} />
              Organization Users
              <span className="users-table-count">{totalUserCount}</span>
            </h3>
            <div className="users-search-wrapper">
              <FontAwesomeIcon icon={faSearch} className="users-search-icon" />
              <input
                type="search"
                className="users-search-input"
                placeholder="Filter users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoComplete="off"
                data-lpignore="true"
                data-form-type="other"
              />
              {searchTerm && (
                <button
                  className="users-search-clear"
                  onClick={() => setSearchTerm('')}
                  aria-label="Clear search"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </div>
          </div>
          {filteredAndSortedUsers.length === 0 && !loadingData ? (
            <div className="users-empty">
              <div className="users-empty-icon">
                <FontAwesomeIcon icon={faSearch} />
              </div>
              <h4>No Users Found</h4>
              <p>
                {searchTerm
                  ? `No users match "${searchTerm}". Try a different search term.`
                  : 'No users found in your organization.'}
              </p>
              {searchTerm && (
                <button
                  className="users-action-btn users-action-btn-admin"
                  onClick={() => setSearchTerm('')}
                  style={{ marginTop: '1rem' }}
                >
                  Clear Filter
                </button>
              )}
            </div>
          ) : (
            <div className="users-table-body">
              <div className="users-table-wrapper">
                <table className="users-table">
                  <thead>
                    <tr>
                      <th onClick={() => requestSort('displayName')} className={sortConfig.key === 'displayName' ? 'sorted' : ''}>
                        User
                        <FontAwesomeIcon icon={getSortIcon('displayName')} className="sort-icon" />
                      </th>
                      <th onClick={() => requestSort('emailVerified')} className={`text-center ${sortConfig.key === 'emailVerified' ? 'sorted' : ''}`}>
                        Verified
                        <FontAwesomeIcon icon={getSortIcon('emailVerified')} className="sort-icon" />
                      </th>
                      <th onClick={() => requestSort('creationTime')} className={sortConfig.key === 'creationTime' ? 'sorted' : ''}>
                        Registered
                        <FontAwesomeIcon icon={getSortIcon('creationTime')} className="sort-icon" />
                      </th>
                      <th onClick={() => requestSort('lastSignInTime')} className={sortConfig.key === 'lastSignInTime' ? 'sorted' : ''}>
                        Last Sign In
                        <FontAwesomeIcon icon={getSortIcon('lastSignInTime')} className="sort-icon" />
                      </th>
                      <th onClick={() => requestSort('admin')} className={`text-center ${sortConfig.key === 'admin' ? 'sorted' : ''}`}>
                        Admin
                        <FontAwesomeIcon icon={getSortIcon('admin')} className="sort-icon" />
                      </th>
                      <th onClick={() => requestSort('platform')} className={sortConfig.key === 'platform' ? 'sorted' : ''}>
                        Platform
                        <FontAwesomeIcon icon={getSortIcon('platform')} className="sort-icon" />
                      </th>
                      <th onClick={() => requestSort('hasTokens')} className={`text-center ${sortConfig.key === 'hasTokens' ? 'sorted' : ''}`}>
                        Push
                        <FontAwesomeIcon icon={getSortIcon('hasTokens')} className="sort-icon" />
                      </th>
                      <th className="text-center" style={{ cursor: 'default' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAndSortedUsers.map((user) => {
                      const isCurrentUser = currentUser?.uid === user.id;
                      const isLoadingThisUserAdmin = actionStatus.userId === user.id && actionStatus.type === 'admin' && actionStatus.loading;
                      const isLoadingThisUserDelete = actionStatus.userId === user.id && actionStatus.type === 'delete' && actionStatus.loading;
                      const isAdminUser = user.admin === true;
                      const isEmailVerified = user.emailVerified === true;

                      return (
                        <tr key={user.id}>
                          <td className="cell-user">
                            <div className="user-name-row">
                              <span className="user-display-name">{user.displayName || 'N/A'}</span>
                              {isCurrentUser && (
                                <span className="users-badge users-badge-you">You</span>
                              )}
                            </div>
                            <div className="user-email">{user.email || '-'}</div>
                            {user.userFid && (
                              <div className="user-family-link">
                                <a
                                  href={`${familyBaseUrl}${user.userFid}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={`View family (ID: ${user.userFid})`}
                                >
                                  View Family <FontAwesomeIcon icon={faExternalLinkAlt} size="xs" />
                                </a>
                              </div>
                            )}
                          </td>
                          <td className="cell-center">
                            {isEmailVerified ? (
                              <span className="users-badge users-badge-verified">
                                <FontAwesomeIcon icon={faEnvelopeCircleCheck} /> Yes
                              </span>
                            ) : (
                              <span className="users-badge users-badge-unverified">
                                <FontAwesomeIcon icon={faExclamationTriangle} /> No
                              </span>
                            )}
                          </td>
                          <td className="cell-nowrap">
                            {user.creationTime ? formatDate(user.creationTime) : (user.cDate ? formatDate(user.cDate) : '-')}
                          </td>
                          <td className="cell-nowrap">
                            {user.lastSignInTime ? formatDate(user.lastSignInTime) : '-'}
                          </td>
                          <td className="cell-center">
                            {isAdminUser ? (
                              <span className="users-badge users-badge-admin">Yes</span>
                            ) : (
                              <span className="users-badge users-badge-user">No</span>
                            )}
                          </td>
                          <td className="cell-nowrap">{user.platform || '-'}</td>
                          <td className="cell-center">
                            {user.hasTokens ? (
                              <span className="users-badge users-badge-push-enabled" title={`${user.tokenCount} device(s) registered`}>
                                <FontAwesomeIcon icon={faBell} /> {user.tokenCount}
                              </span>
                            ) : (
                              <span className="users-badge users-badge-push-disabled" title="No push notifications">
                                <FontAwesomeIcon icon={faBellSlash} />
                              </span>
                            )}
                          </td>
                          <td className="cell-actions">
                            {isLoadingThisUserAdmin || isLoadingThisUserDelete ? (
                              <FontAwesomeIcon icon={faSpinner} spin title="Processing..." />
                            ) : (
                              <div className="users-actions-wrapper">
                                <button
                                  type="button"
                                  className={`users-action-btn ${isAdminUser ? 'users-action-btn-revoke' : 'users-action-btn-admin'}`}
                                  title={isAdminUser ? `Remove admin privileges from ${user.email}` : `Make ${user.email} an admin`}
                                  aria-label={isAdminUser ? `Remove admin privileges from ${user.email}` : `Make ${user.email} an admin`}
                                  onClick={() => handleToggleAdmin(user.id, user.email, isAdminUser)}
                                  disabled={isCurrentUser || isLoadingThisUserAdmin || isLoadingThisUserDelete}
                                >
                                  <FontAwesomeIcon icon={faUserShield} />
                                  <span>{isAdminUser ? 'Revoke' : 'Admin'}</span>
                                </button>

                                <button
                                  type="button"
                                  className="users-action-btn users-action-btn-delete"
                                  title={`Delete user ${user.email}`}
                                  aria-label={`Delete user ${user.email}`}
                                  onClick={() => handleDeleteUser(user.id, user.email)}
                                  disabled={isCurrentUser || isLoadingThisUserAdmin || isLoadingThisUserDelete}
                                >
                                  <FontAwesomeIcon icon={faTrashAlt} />
                                  <span>Delete</span>
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
          </>
        )}

        {activeTab === 'unregistered' && (
          <div className="users-table-card">
            <div className="users-table-header">
              <h3 className="users-table-title">
                <FontAwesomeIcon icon={faEnvelope} />
                Members Without Accounts
                <span className="users-table-count">{unregisteredCount}</span>
              </h3>
            </div>
            {unregisteredMembers.length === 0 ? (
              <div className="users-empty">
                <div className="users-empty-icon">
                  <FontAwesomeIcon icon={faCheckCircle} />
                </div>
                <h4>All Set!</h4>
                <p>All members with email addresses have registered accounts.</p>
              </div>
            ) : (
              <div className="users-table-body">
                <p className="users-unregistered-intro">
                  These members have email addresses on file but haven't created a Church Aware account yet.
                  They can receive email announcements but won't receive push notifications.
                </p>
                <div className="users-table-wrapper">
                  <table className="users-table">
                    <thead>
                      <tr>
                        <th>First Name</th>
                        <th>Last Name</th>
                        <th>Email</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unregisteredMembers.map((member) => {
                        const surname = familyMap[member.familyId]?.surname || '';
                        return (
                          <tr key={member.id}>
                            <td className="cell-user">
                              <div className="user-display-name">{member.name || 'Unknown'}</div>
                              {member.familyId && (
                                <div className="user-family-link">
                                  <Link to={`/families/${member.familyId}`}>
                                    View Family <FontAwesomeIcon icon={faExternalLinkAlt} size="xs" />
                                  </Link>
                                </div>
                              )}
                            </td>
                            <td>{surname}</td>
                            <td className="cell-nowrap">
                              <a href={`mailto:${member.email}`} className="user-email-link">
                                {member.email}
                              </a>
                            </td>
                            <td className="cell-actions">
                              <a
                                href={`mailto:${member.email}?subject=Join%20Church%20Aware&body=Hi%20${encodeURIComponent(member.name || '')}%2C%0A%0AYou%20can%20create%20your%20Church%20Aware%20account%20here%3A%0A${encodeURIComponent(createUsersBaseUrl + userOrgId)}%0A%0AThanks!`}
                                className="users-action-btn users-action-btn-admin"
                                title={`Send invite to ${member.email}`}
                              >
                                <FontAwesomeIcon icon={faEnvelope} />
                                <span>Invite</span>
                              </a>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default UserManagement;
